import { useState, useEffect } from "react";
import { Button, Input, Form, Alert } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  KeyOutlined,
  MailOutlined,
} from "@ant-design/icons";
import "../scss/reset-password-link.scss";
import AuthService from "../utils/api/auth";

import { useNavigate } from "react-router-dom";

export default function ResetPasswordLink() {
  const [feedback, setFeedback] = useState({
    isSuccess: false,
    isError: false,
    message: "",
  });
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [load, setLoad] = useState(false);
  const [submitableForm, setSubmitableForm] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmitableForm(true);
        },
        () => {
          setSubmitableForm(false);
        }
      );
  }, [formValues]);

  const onFinish = (values) => {
    setLoad(true);
    setFeedback({ isSuccess: false, isError: false, message: "" });
    
    AuthService.PasswordResetRequest(values.email)
      .then((response) => {
        const successValue = response?.data?.success;
        const messageText = String(response?.data?.message || "");
        const isSuccessfulResponse =
          successValue === true ||
          successValue === "true" ||
          successValue === 1 ||
          successValue === "1" ||
          /correo enviado|instrucciones|enlace de recuperacion|email sent/i.test(messageText);

        if (isSuccessfulResponse) {
          setSentEmail(values.email);
          setFeedback({ isSuccess: false, isError: false, message: "" });
          setConfirmationVisible(true);
          form.resetFields();
          setLoad(false);
        } else {
          setFeedback({
            isError: true,
            message: response.data.message,
          });
          setLoad(false);
        }
      })
      .catch((error) => {
        setFeedback({
          isError: true,
          message: "Hubo un error al enviar el correo, intenta de nuevo.",
        });
        setLoad(false);
      });
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        <div className="reset-card__header">
          <div className="reset-card__icon-wrap">
            <KeyOutlined className="reset-card__icon" />
          </div>

          <h1 className="reset-card__title">¿Olvidaste tu contraseña?</h1>

          <p className="reset-card__subtitle">
            No te preocupes, te ayudaremos a recuperarla
          </p>

          <div className="reset-card__circle reset-card__circle--top" />
          <div className="reset-card__circle reset-card__circle--bottom" />
        </div>

        <div className="reset-card__content">
          <Form onFinish={onFinish} form={form} layout="vertical">
            {feedback.isError && (
              <div className="reset-alert-wrap">
                <Alert message={feedback.message} type="error" closable />
              </div>
            )}

            <Form.Item
              label={<span className="reset-input-label">Correo electronico</span>}
              name="email"
              rules={[
                {
                  required: true,
                  message: "Favor de ingresar tu correo electronico",
                },
                {
                  type: "email",
                  message: "Esto no parece un correo electronico",
                },
              ]}
              className="reset-input-item"
            >
              <Input
                className="reset-input"
                prefix={<MailOutlined className="reset-input-icon" />}
                placeholder="tu@email.com"
                size="large"
              />
            </Form.Item>

            <p className="reset-input-help">Ingresa el correo asociado a tu cuenta</p>

            <Form.Item className="reset-submit-item">
              <Button
                className="reset-submit-btn"
                type="primary"
                htmlType="submit"
                block={true}
                loading={load}
                disabled={!submitableForm}
              >
                Enviar instrucciones
              </Button>
            </Form.Item>

            <div className="reset-back-wrap">
              <Button
                className="reset-back-btn"
                type="link"
                onClick={() => {
                  navigate("/");
                }}
              >
                <ArrowLeftOutlined className="reset-back-icon" />
                Volver al inicio de sesion
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <p className="reset-support-text">
        ¿Necesitas ayuda? {" "}
        <a className="reset-support-link" href="mailto:soporte@epno.com.mx">
          Contacta con soporte
        </a>
      </p>

      {confirmationVisible && (
        <div className="confirm-overlay">
          <div className="confirm-card">
            <div className="confirm-card__header">
              <div className="confirm-card__mail-wrap">
                <MailOutlined className="confirm-card__mail-icon" />
              </div>

              <h2 className="confirm-card__title">
                ¡Revisa tu correo!
              </h2>

              <p className="confirm-card__subtitle">
                Te hemos enviado las instrucciones
              </p>

              <div className="confirm-card__circle confirm-card__circle--top" />
              <div className="confirm-card__circle confirm-card__circle--bottom" />
            </div>

            <div className="confirm-card__content">
              <div className="confirm-card__panel">
                <CheckCircleFilled className="confirm-card__check-icon" />
                <h3 className="confirm-card__panel-title">
                  Correo enviado exitosamente
                </h3>
                <p className="confirm-card__panel-text">
                  Hemos enviado un enlace de recuperacion a:
                </p>
                <p className="confirm-card__email">{sentEmail}</p>

                <div className="confirm-card__note-box">
                  <p className="confirm-card__note-title">Nota importante:</p>
                  <ul className="confirm-card__note-list">
                    <li>El enlace expira en 24 horas</li>
                    <li>Revisa tu carpeta de spam si no lo encuentras</li>
                    <li>Si no recibes el correo, intenta nuevamente</li>
                  </ul>
                </div>
              </div>

              <Button
                className="confirm-card__primary-btn"
                type="primary"
                block={true}
                onClick={() => {
                  navigate("/");
                }}
              >
                Ir al inicio de sesion
              </Button>

              <Button
                className="confirm-card__secondary-btn"
                type="link"
                block={true}
                onClick={() => {
                  setConfirmationVisible(false);
                  setFeedback({ isSuccess: false, isError: false, message: "" });
                  form.resetFields();
                }}
              >
                Intentar con otro correo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
